import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Link
} from '@mui/material';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Link as LinkIcon, Inventory2, Close, Print, ReceiptLong, ViewColumn } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { shipmentsInService } from '../services/shipmentsInService';
import { attachmentService } from '../services/attachmentService';
import { useAuth } from '../hooks/useAuth';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { printWithFilename } from '../utils/printUtils';
import { formatDateOnly, formatLocalTodayYmd, sortTimestampFromDateOnly } from '../utils/dateUtils';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  formatShipmentPoDisplay,
  formatLinkedPoDisplay
} from '../components/procurement/ProcurementSummaryBar';

const emptyReceiverForm = () => ({
  receiver_no: '',
  receiver_date: formatLocalTodayYmd(),
  vendor_id: null,
  name: '',
  po_number: '',
  purchase_order_id: null,
  billed: false,
  pack_list: '',
  vendor_type: '',
  ship_via: '',
  notes: ''
});

const rowToForm = (row) => ({
  receiver_no: row.receiver_no ?? '',
  receiver_date: (row.receiver_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
  vendor_id: row.vendor_id ?? null,
  name: row.name ?? '',
  po_number: row.po_number ?? '',
  purchase_order_id: row.purchase_order_id ?? null,
  invoice_id: row.invoice_id ?? null,
  billed: !!row.billed,
  pack_list: row.pack_list ?? '',
  vendor_type: row.vendor_type ?? '',
  ship_via: row.ship_via ?? '',
  notes: row.notes ?? ''
});

/** Map line item for API create/update */
const lineItemToPayload = (it) => ({
  purchase_order_item_id: it.purchase_order_item_id ?? null,
  line_number: it.line_number ?? 0,
  part_id: it.part_id ?? null,
  part_number: it.part_number ?? null,
  description: it.description ?? null,
  quantity_ordered: Number(it.quantity_ordered) || 0,
  quantity_received: Number(it.quantity_received) || 0,
  unit: it.unit || 'EA',
  unit_price: Number(it.unit_price) || 0,
  line_total: Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0)),
  notes: it.notes ?? null
});

const ProcurementReceiving = () => {
  const DEFAULT_RECEIVING_COL_WIDTHS = {
    receiver: 170,
    linkedPo: 130,
    date: 120,
    name: 220,
    po: 160,
    billed: 90,
    packList: 130,
    vendorType: 130,
    shipVia: 140,
    actions: 120
  };
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [billedFilter, setBilledFilter] = useState(''); // '' | 'true' | 'false'
  const [receivers, setReceivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stubMessage, setStubMessage] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [receiverForm, setReceiverForm] = useState(emptyReceiverForm);
  const [saving, setSaving] = useState(false);
  const [nextNoLoading, setNextNoLoading] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [poLookupLoading, setPoLookupLoading] = useState(false);
  const [poLookupNumber, setPoLookupNumber] = useState('');
  const [receiveIntoStockLoading, setReceiveIntoStockLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const {
    colWidths,
    setColWidths,
    startResize,
    saveColWidths,
    resetColWidths
  } = useResizableColumns({
    defaultWidths: DEFAULT_RECEIVING_COL_WIDTHS,
    storageKey: 'wm_procurement_receiving_col_widths',
    minWidths: {
      receiver: 120,
      linkedPo: 100,
      date: 90,
      name: 160,
      po: 120,
      billed: 80,
      packList: 100,
      vendorType: 100,
      shipVia: 100,
      actions: 100
    }
  });
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [sortKey, setSortKey] = useState('receiver_no');
  const [sortDirection, setSortDirection] = useState('desc');
  const navigate = useNavigate();
  const { isManagerOrAdmin } = useAuth();
  const canReceiveIntoStock = isManagerOrAdmin && typeof isManagerOrAdmin === 'function' && isManagerOrAdmin();

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedReceivers = useMemo(() => {
    const list = [...receivers];
    const getter = (row) => {
      switch (sortKey) {
        case 'receiver_no': return row.identifier ?? row.receiver_no ?? '';
        case 'receiver_date': return sortTimestampFromDateOnly(row.receiver_date);
        case 'name': return row.name ?? '';
        case 'purchase_order_identifier':
          return row.purchase_order_identifier ?? (row.purchase_order_id != null ? String(row.purchase_order_id) : '');
        case 'po_number': return row.po_number ?? '';
        case 'billed': return row.billed ? 1 : 0;
        case 'pack_list': return row.pack_list ?? '';
        case 'vendor_type': return row.vendor_type ?? '';
        case 'ship_via': return row.ship_via ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [receivers, sortKey, sortDirection]);

  const loadReceivers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        search: searchTerm || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        billed: billedFilter || undefined,
        limit: 100
      };
      const res = await shipmentsInService.getAll(params);
      const list = res.data ?? [];
      setReceivers(list);
      setSelectedReceiver(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading shipments in:', err);
      setError(err.response?.data?.message || 'Failed to load shipments in');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReceivers();
  }, []);

  useEffect(() => {
    // list page only; attachments and create/edit are handled in detail page
    setAttachments([]);
  }, []);

  // Prevent browser opening files dropped outside drop-zone.
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  const handleSearch = () => {
    loadReceivers();
  };

  const openNewDialog = async () => {
    setReceiverForm(emptyReceiverForm());
    setLineItems([]);
    setPoLookupNumber('');
    setNewDialogOpen(true);
    setNextNoLoading(true);
    setError('');
    try {
      const res = await shipmentsInService.getNextNo();
      const next = res?.data?.receiver_no ?? `R${Date.now().toString(36).slice(-5).toUpperCase()}`;
      setReceiverForm((f) => ({ ...f, receiver_no: next, receiver_date: formatLocalTodayYmd() }));
    } catch (err) {
      console.error('Error loading next shipment number:', err);
      setError(err.response?.data?.message || 'Could not get next shipment number');
      setReceiverForm((f) => ({ ...f, receiver_date: formatLocalTodayYmd() }));
    } finally {
      setNextNoLoading(false);
    }
  };

  const handleLoadFromPo = async () => {
    const poNumber = (poLookupNumber || '').trim();
    if (!poNumber) {
      setError('Enter a P.O. number to load.');
      return;
    }
    setPoLookupLoading(true);
    setError('');
    try {
      const res = await shipmentsInService.getPrefillFromPo(poNumber);
      const { header, items } = res?.data ?? {};
      if (header) {
        setReceiverForm((f) => ({
          ...f,
          vendor_id: header.vendor_id ?? f.vendor_id,
          name: header.name ?? f.name,
          po_number: header.po_number ?? poNumber,
          purchase_order_id: header.purchase_order_id ?? f.purchase_order_id,
          ship_via: header.ship_via ?? f.ship_via,
          notes: header.notes ?? f.notes
        }));
      }
      if (Array.isArray(items) && items.length > 0) {
        setLineItems(items.map((it, idx) => ({
          ...it,
          line_number: it.line_number ?? idx + 1,
          quantity_received: it.quantity_received ?? 0
        })));
      } else {
        setLineItems([]);
      }
    } catch (err) {
      console.error('Error loading from PO:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not load purchase order.');
    } finally {
      setPoLookupLoading(false);
    }
  };

  const handleCreateReceiver = async () => {
    const { receiver_no, receiver_date, name } = receiverForm;
    if (!receiver_no?.trim() || !receiver_date || !name?.trim()) {
      setError('Shipment, Date, and Name are required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = { ...receiverForm };
      delete payload.billed;
      delete payload.invoice_id;
      if (lineItems.length > 0) {
        payload.items = lineItems.map(lineItemToPayload);
      }
      await shipmentsInService.create(payload);
      setNewDialogOpen(false);
      loadReceivers();
    } catch (err) {
      console.error('Error creating receiver:', err);
      setError(err.response?.data?.message || 'Failed to create receiver');
    } finally {
      setSaving(false);
    }
  };

  const loadAttachments = async (receiverId) => {
    if (!receiverId) {
      setAttachments([]);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForReceiver(receiverId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error('Error loading shipment attachments:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!selectedReceiver?.id || !files?.length) return;
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }
    try {
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, receiverId: selectedReceiver.id });
      }
      await loadAttachments(selectedReceiver.id);
    } catch (err) {
      console.error('Error uploading receiver attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    }
  };

  const handleFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDraggingOverAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedReceiver?.id) return;
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (att) => {
    try {
      setAttachmentsError('');
      const res = await attachmentService.download(att.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  const openEditDialog = async (row) => {
    if (!row) return;
    setSelectedReceiver(row);
    setEditDialogOpen(true);
    setError('');
    try {
      const res = await shipmentsInService.getById(row.id);
      const data = res?.data;
      if (data) {
        setReceiverForm(rowToForm(data));
        setLineItems((data.items || []).map((it) => ({
          ...it,
          quantity_received: it.quantity_received ?? 0
        })));
      } else {
        setReceiverForm(rowToForm(row));
        setLineItems([]);
      }
    } catch (err) {
      console.error('Error loading shipment:', err);
      setError(err.response?.data?.message || 'Failed to load shipment');
      setReceiverForm(rowToForm(row));
      setLineItems([]);
    }
  };

  const handleUpdateReceiver = async () => {
    if (!selectedReceiver?.id) return;
    const { receiver_no, receiver_date, name } = receiverForm;
    if (!receiver_no?.trim() || !receiver_date || !name?.trim()) {
      setError('Shipment, Date, and Name are required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = { ...receiverForm };
      delete payload.billed;
      delete payload.invoice_id;
      payload.items = lineItems.map(lineItemToPayload);
      await shipmentsInService.update(selectedReceiver.id, payload);
      setEditDialogOpen(false);
      setSelectedReceiver(null);
      loadReceivers();
    } catch (err) {
      console.error('Error updating shipment:', err);
      setError(err.response?.data?.message || 'Failed to update shipment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReceiver = async () => {
    if (!selectedReceiver?.id) return;
    try {
      setSaving(true);
      setError('');
      await shipmentsInService.delete(selectedReceiver.id);
      setDeleteConfirmOpen(false);
      setSelectedReceiver(null);
      loadReceivers();
    } catch (err) {
      console.error('Error deleting shipment:', err);
      setError(err.response?.data?.message || 'Failed to delete shipment');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const handleReceiveIntoStock = async () => {
    if (!selectedReceiver?.id || !canReceiveIntoStock) return;
    setStubMessage('Not yet implemented. Stub for inventory updating.');
  };

  const handlePrintReceiver = (row) => {
    if (!row) return;
    setSelectedReceiver(row);
    const receiverFilename = row.identifier || row.receiver_no || 'Shipment-Received';
    printWithFilename(receiverFilename, { delayMs: 200 });
  };

  const handlePrintInvoice = (row) => {
    if (!row?.invoice_id) return;
    navigate(`/procurement/invoices?invoice=${row.invoice_id}&print=1`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Shipments Received
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canReceiveIntoStock && (
            <Tooltip title="Not yet implemented. Stub for inventory updating.">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<Inventory2 />}
                  onClick={handleReceiveIntoStock}
                  disabled={!selectedReceiver || receiveIntoStockLoading}
                >
                  {receiveIntoStockLoading ? 'Putting away…' : 'Receive into stock'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedReceiver && navigate(`/procurement/shipments-in/${selectedReceiver.id}`)} disabled={!selectedReceiver}>
            Edit
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedReceiver && setDeleteConfirmOpen(true)} disabled={!selectedReceiver}>
            Delete
          </Button>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={() => navigate('/procurement/shipments-in/new')}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {stubMessage && (
        <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setStubMessage('')}>
          {stubMessage}
        </Alert>
      )}

      <ProcurementSummaryBar>
        <ProcurementSummaryField label="Shipment">
          <Box sx={{ fontWeight: 600 }}>{selectedReceiver?.identifier ?? selectedReceiver?.receiver_no ?? '—'}</Box>
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Date">
          {selectedReceiver ? formatDate(selectedReceiver.receiver_date) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Vendor / received from">{selectedReceiver?.name ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="P.O.">
          {selectedReceiver ? formatLinkedPoDisplay(selectedReceiver) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Cust. P.O.">{selectedReceiver ? formatShipmentPoDisplay(selectedReceiver) : '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="Billed">{selectedReceiver?.billed ? 'Yes' : 'No'}</ProcurementSummaryField>
        <ProcurementSummaryField label="Vendor type">{selectedReceiver?.vendor_type ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="Ship via">{selectedReceiver?.ship_via ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="Notes" wide>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedReceiver?.notes ?? '—'}</Box>
        </ProcurementSummaryField>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={() => setSelectedReceiver(null)}
            disabled={!selectedReceiver}
            title="Clear selection"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </ProcurementSummaryBar>

      <Paper sx={{ p: 1.5, mb: 1.5, border: '1px dashed', borderColor: 'divider', backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Received Shipment Documents</Typography>
        <Typography variant="body2" color="text.secondary">
          Open a Shipment Received record to add/view documents. Uploads are handled on the full detail page.
        </Typography>
      </Paper>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by Shipment..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Date from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="Date to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="receiving-billed-label">Billed</InputLabel>
            <Select
              labelId="receiving-billed-label"
              label="Billed"
              value={billedFilter}
              onChange={(e) => setBilledFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
          <IconButton
            size="small"
            onClick={() => setColumnsDialogOpen(true)}
            title="Adjust column widths"
            sx={{ ml: 0.5 }}
          >
            <ViewColumn fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <ResizableHeaderCell width={colWidths.receiver} onResizeStart={(e) => startResize('receiver', e)} sortable sortActive={sortKey === 'receiver_no'} sortDirection={sortDirection} onSort={() => handleSort('receiver_no')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Shipment</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.linkedPo} onResizeStart={(e) => startResize('linkedPo', e)} sortable sortActive={sortKey === 'purchase_order_identifier'} sortDirection={sortDirection} onSort={() => handleSort('purchase_order_identifier')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>P.O.</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.date} onResizeStart={(e) => startResize('date', e)} sortable sortActive={sortKey === 'receiver_date'} sortDirection={sortDirection} onSort={() => handleSort('receiver_date')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Date</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.name} onResizeStart={(e) => startResize('name', e)} sortable sortActive={sortKey === 'name'} sortDirection={sortDirection} onSort={() => handleSort('name')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Received From</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.po} onResizeStart={(e) => startResize('po', e)} sortable sortActive={sortKey === 'po_number'} sortDirection={sortDirection} onSort={() => handleSort('po_number')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Cust. P.O.</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.billed} onResizeStart={(e) => startResize('billed', e)} sortable sortActive={sortKey === 'billed'} sortDirection={sortDirection} onSort={() => handleSort('billed')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Billed</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.packList} onResizeStart={(e) => startResize('packList', e)} sortable sortActive={sortKey === 'pack_list'} sortDirection={sortDirection} onSort={() => handleSort('pack_list')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Pack List</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.vendorType} onResizeStart={(e) => startResize('vendorType', e)} sortable sortActive={sortKey === 'vendor_type'} sortDirection={sortDirection} onSort={() => handleSort('vendor_type')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Vendor Type</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.shipVia} onResizeStart={(e) => startResize('shipVia', e)} sortable sortActive={sortKey === 'ship_via'} sortDirection={sortDirection} onSort={() => handleSort('ship_via')}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Ship Via</Typography></ResizableHeaderCell>
              <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Actions</Typography></ResizableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : receivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No shipment records yet. Use New to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedReceivers.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedReceiver?.id === row.id}
                  onClick={() => setSelectedReceiver(row)}
                  onDoubleClick={() => navigate(`/procurement/shipments-in/${row.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.receiver }}>{row.identifier ?? row.receiver_no ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.linkedPo }} onClick={(e) => e.stopPropagation()}>
                    {row.purchase_order_id ? (
                      <Link
                        component={RouterLink}
                        to={`/procurement/po/${row.purchase_order_id}`}
                        variant="body2"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatLinkedPoDisplay(row)}
                      </Link>
                    ) : (
                      formatLinkedPoDisplay(row)
                    )}
                  </TableCell>
                  <TableCell sx={{ width: colWidths.date }}>{formatDate(row.receiver_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.name }}>{row.name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.po }}>{formatShipmentPoDisplay(row)}</TableCell>
                  <TableCell sx={{ width: colWidths.billed }}>{row.billed ? 'Yes' : 'No'}</TableCell>
                  <TableCell sx={{ width: colWidths.packList }}>{row.pack_list ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.vendorType }}>{row.vendor_type ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.shipVia }}>{row.ship_via ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.actions }} onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <IconButton size="small" color="primary" title="Print received shipment" onClick={() => handlePrintReceiver(row)}>
                        <Print fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color={row.invoice_id ? 'primary' : 'inherit'}
                        title={row.invoice_id ? `Print invoice ${row.invoice_identifier || ''}`.trim() : 'No invoice linked'}
                        onClick={() => handlePrintInvoice(row)}
                        disabled={!row.invoice_id}
                      >
                        <ReceiptLong fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New Shipment dialog */}
      <Dialog open={columnsDialogOpen} onClose={() => setColumnsDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust table columns</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1.25, mt: 0.5 }}>
            <TextField size="small" type="number" label="Shipment width" value={colWidths.receiver} onChange={(e) => setColWidths((p) => ({ ...p, receiver: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="P.O. column width" value={colWidths.linkedPo} onChange={(e) => setColWidths((p) => ({ ...p, linkedPo: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Date width" value={colWidths.date} onChange={(e) => setColWidths((p) => ({ ...p, date: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Name width" value={colWidths.name} onChange={(e) => setColWidths((p) => ({ ...p, name: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Cust. P.O. width" value={colWidths.po} onChange={(e) => setColWidths((p) => ({ ...p, po: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Billed width" value={colWidths.billed} onChange={(e) => setColWidths((p) => ({ ...p, billed: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Pack List width" value={colWidths.packList} onChange={(e) => setColWidths((p) => ({ ...p, packList: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Vendor Type width" value={colWidths.vendorType} onChange={(e) => setColWidths((p) => ({ ...p, vendorType: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Ship Via width" value={colWidths.shipVia} onChange={(e) => setColWidths((p) => ({ ...p, shipVia: Number(e.target.value) || 0 }))} />
            <TextField size="small" type="number" label="Actions width" value={colWidths.actions} onChange={(e) => setColWidths((p) => ({ ...p, actions: Number(e.target.value) || 0 }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetColWidths}>Reset</Button>
          <Button onClick={() => setColumnsDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              saveColWidths(colWidths);
              setColumnsDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newDialogOpen} onClose={() => !saving && setNewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Shipment Received</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Load from Purchase Order</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Optional. Skip this to record a receipt without a PO — it will be stored as <strong>UNKNOWN PO</strong>.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    label="P.O. Number"
                    placeholder="Enter PO # then click Load"
                    value={poLookupNumber}
                    onChange={(e) => setPoLookupNumber(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleLoadFromPo())}
                    sx={{ minWidth: 160 }}
                  />
                  <Button variant="outlined" size="small" onClick={handleLoadFromPo} disabled={poLookupLoading}>
                    {poLookupLoading ? 'Loading…' : 'Load from PO'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Shipment"
                value={receiverForm.receiver_no}
                onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_no: e.target.value }))}
                disabled={nextNoLoading}
                helperText={nextNoLoading ? 'Loading…' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Date"
                type="date"
                value={receiverForm.receiver_date}
                onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={receiverForm.name}
                onChange={(e) => setReceiverForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cust. P.O."
                value={receiverForm.po_number}
                onChange={(e) => setReceiverForm((f) => ({ ...f, po_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Billed: No — Link an invoice in Procurement → Invoices to mark billed.
              </Typography>
              <Button size="small" startIcon={<LinkIcon />} onClick={() => { setNewDialogOpen(false); navigate('/procurement/invoices'); }}>
                Go to Invoices
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Pack List"
                value={receiverForm.pack_list}
                onChange={(e) => setReceiverForm((f) => ({ ...f, pack_list: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Vendor Type"
                value={receiverForm.vendor_type}
                onChange={(e) => setReceiverForm((f) => ({ ...f, vendor_type: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Ship Via"
                value={receiverForm.ship_via}
                onChange={(e) => setReceiverForm((f) => ({ ...f, ship_via: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={receiverForm.notes}
                onChange={(e) => setReceiverForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {lineItems.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Line items (from PO)</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><Typography variant="caption">Part / Description</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Qty ordered</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Qty received</Typography></TableCell>
                        <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((it, idx) => (
                        <TableRow key={it.purchase_order_item_id ?? idx}>
                          <TableCell>
                            <Typography variant="body2">{it.part_number || it.description || '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">{Number(it.quantity_ordered) || 0}</TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              inputProps={{ min: 0, step: 0.001 }}
                              value={it.quantity_received ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setLineItems((prev) => prev.map((item, i) => i === idx ? { ...item, quantity_received: v === '' ? 0 : parseFloat(v) } : item));
                              }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell>{it.unit || 'EA'}</TableCell>
                          <TableCell align="right">${(Number(it.unit_price) || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">${((Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0)))).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" sx={{ mt: 0.75, textAlign: 'right', fontWeight: 600 }}>
                  Total: ${lineItems.reduce((sum, it) => sum + (Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0))), 0).toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateReceiver} disabled={saving || nextNoLoading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Shipment dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && (setEditDialogOpen(false), setSelectedReceiver(null))} maxWidth="md" fullWidth>
        <DialogTitle>Edit Shipment Received</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Shipment"
                value={receiverForm.receiver_no}
                onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_no: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Date"
                type="date"
                value={receiverForm.receiver_date}
                onChange={(e) => setReceiverForm((f) => ({ ...f, receiver_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={receiverForm.name}
                onChange={(e) => setReceiverForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cust. P.O."
                value={receiverForm.po_number}
                onChange={(e) => setReceiverForm((f) => ({ ...f, po_number: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {receiverForm.invoice_id ? (
                <Typography variant="body2">Billed: Yes — Linked to invoice. Mark not billed by unlinking in Procurement → Invoices.</Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">Billed: No — Link an invoice in Procurement → Invoices to mark billed.</Typography>
                  <Button size="small" startIcon={<LinkIcon />} onClick={() => { setEditDialogOpen(false); setSelectedReceiver(null); navigate('/procurement/invoices'); }}>
                    Go to Invoices
                  </Button>
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Pack List"
                value={receiverForm.pack_list}
                onChange={(e) => setReceiverForm((f) => ({ ...f, pack_list: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Vendor Type"
                value={receiverForm.vendor_type}
                onChange={(e) => setReceiverForm((f) => ({ ...f, vendor_type: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Ship Via"
                value={receiverForm.ship_via}
                onChange={(e) => setReceiverForm((f) => ({ ...f, ship_via: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={receiverForm.notes}
                onChange={(e) => setReceiverForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {(lineItems.length > 0 || editDialogOpen) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Line items</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><Typography variant="caption">Part / Description</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Qty ordered</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Qty received</Typography></TableCell>
                        <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                        <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary">No line items</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        lineItems.map((it, idx) => (
                          <TableRow key={it.id ?? it.purchase_order_item_id ?? idx}>
                            <TableCell>
                              <Typography variant="body2">{it.part_number || it.description || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">{Number(it.quantity_ordered) || 0}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                inputProps={{ min: 0, step: 0.001 }}
                                value={it.quantity_received ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setLineItems((prev) => prev.map((item, i) => i === idx ? { ...item, quantity_received: v === '' ? 0 : parseFloat(v) } : item));
                                }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>{it.unit || 'EA'}</TableCell>
                            <TableCell align="right">${(Number(it.unit_price) || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">${((Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0)))).toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="body2" sx={{ mt: 0.75, textAlign: 'right', fontWeight: 600 }}>
                  Total: ${lineItems.reduce((sum, it) => sum + (Number(it.line_total) || ((Number(it.quantity_ordered) || 0) * (Number(it.unit_price) || 0))), 0).toFixed(2)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedReceiver(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateReceiver} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Shipment Received</DialogTitle>
        <DialogContent>
          <Typography>
            Delete shipment <strong>{selectedReceiver?.identifier ?? selectedReceiver?.receiver_no ?? selectedReceiver?.id}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteReceiver} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementReceiving;
